import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet';

import {
  TextareaWithConfirm,
  DateInputWithConfirm,
  InputWithConfirm,
  getStringFromDate,
  getDateFromString,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import { ApplicationState } from '../../../../../store';
import * as StudentActions from '../../../../../store/student/actions';
import * as VPFActions from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../../middleware/actions';
import { isValidDate } from '../../../../../utilities/validation';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../../store/ui/actions';
import { IDate } from '../../../../../types';
import { titleAppend } from '../../../../../utilities/head';

const VisaPrepFormUniEnrolmentPage1 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => {
    dispatch(StudentActions.saveVisaPrepForm(Pages.PAGE7_1));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(VPFActions.createUpdateVPIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  // state props from store
  const visaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);
  const {
    hasReceivedCas,
    studiesConfirmationNumber,
    courseEndDate: storeCourseEndDate,
    alreadyStartedStudying,
    studyAddress,
    assessedBySponsor
  } = visaPrepForm;

  // constants
  const currentStep = 1;
  const steps = 3;
  const sectionTitle = 'University Enrolment';

  // component state
  const [complete, setComplete] = useState(false);
  const [courseEndDate, setCourseEndDate] = useState<IDate>({ DD: null, MM: null, YYYY: null });
  const [courseEndDateError, setCourseEndDateError] = useState('');

  useEffect(() => {
    if (storeCourseEndDate && courseEndDate.DD === null && courseEndDate.MM === null && courseEndDate.YYYY === null) {
      const spouseDobFormatted = getDateFromString(storeCourseEndDate);
      setCourseEndDate(spouseDobFormatted);
    }
    const { complete: newComplete } = getPageStatus({
      hasReceivedCas,
      studiesConfirmationNumber,
      courseEndDate: storeCourseEndDate,
      alreadyStartedStudying,
      studyAddress,
      assessedBySponsor,
      courseEndDateError
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    hasReceivedCas,
    studiesConfirmationNumber,
    storeCourseEndDate,
    courseEndDate.DD,
    courseEndDate.MM,
    courseEndDate.YYYY,
    alreadyStartedStudying,
    studyAddress,
    assessedBySponsor,
    courseEndDateError
  ]);

  const validateDate = (dateValue: IDate) => {
    const { YYYY, DD, MM } = dateValue;
    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        setCourseEndDateError(`Doesn't look like a valid date`);
      } else {
        onChange(VQ.COURSE_END_DATE, getStringFromDate(dateValue));
        setCourseEndDateError('');
      }
    }
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onPrevious={onPrevious}
      onNext={onNext}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <RadioOptionsWithConfirm
        key={VQ.HAS_RECEIVED_CAS}
        id={VQ.HAS_RECEIVED_CAS}
        label={'Have you received your CAS yet?'}
        onClick={(question: IQuestion) => onChange(VQ.HAS_RECEIVED_CAS, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasReceivedCas === true
          },
          {
            char: 'N',
            label: 'No',
            selected: hasReceivedCas === false
          }
        ]}
      />

      {hasReceivedCas === true ? (
        <InputWithConfirm
          key={VQ.STUDIES_CONFIRMATION_NUMBER}
          id={VQ.STUDIES_CONFIRMATION_NUMBER}
          value={studiesConfirmationNumber}
          label={'What is your Confirmation of Acceptance for Studies Number?'}
          onChange={(evt: any) => {
            onChange(VQ.STUDIES_CONFIRMATION_NUMBER, evt.currentTarget.value);
          }}
        />
      ) : null}

      {hasReceivedCas === true ? (
        <DateInputWithConfirm
          key={VQ.COURSE_END_DATE}
          id={VQ.COURSE_END_DATE}
          label={'Course end date'}
          value={courseEndDate}
          errorText={courseEndDateError}
          onChangeDD={(evt: any) => {
            setCourseEndDate({
              ...courseEndDate,
              DD: evt.currentTarget.value
            });
          }}
          onChangeMM={(evt: any) => {
            setCourseEndDate({
              ...courseEndDate,
              MM: evt.currentTarget.value
            });
          }}
          onChangeYY={(evt: any) => {
            const value: string = evt.currentTarget.value;
            if (value.length === 4) {
              setCourseEndDate({
                ...courseEndDate,
                YYYY: value
              });

              validateDate({
                ...courseEndDate,
                YYYY: value
              });
            } else {
              setCourseEndDate({
                ...courseEndDate,
                YYYY: value
              });
            }
          }}
          onBlur={(evt: any) =>
            validateDate({
              ...courseEndDate
            })
          }
        />
      ) : null}

      <RadioOptionsWithConfirm
        key={VQ.ALREADY_STARTED_STUDYING}
        id={VQ.ALREADY_STARTED_STUDYING}
        label={'Have you already started studying this course?'}
        onClick={(question: IQuestion) => onChange(VQ.ALREADY_STARTED_STUDYING, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: alreadyStartedStudying === true
          },
          {
            char: 'N',
            label: 'No',
            selected: alreadyStartedStudying === false
          }
        ]}
      />

      {hasReceivedCas === true ? (
        <TextareaWithConfirm
          key={VQ.STUDY_ADDRESS}
          id={VQ.STUDY_ADDRESS}
          value={studyAddress}
          label={'What is the address of the main site of study?'}
          onChange={(evt: any) => onChange(VQ.STUDY_ADDRESS, evt.currentTarget.value)}
        />
      ) : null}

      <RadioOptionsWithConfirm
        key={VQ.ASSESSED_BY_SPONSOR}
        id={VQ.ASSESSED_BY_SPONSOR}
        label={
          'Have you been assessed by the sponsor by other means, for example references or a portfolio of artwork?'
        }
        onClick={(question: IQuestion) => onChange(VQ.ASSESSED_BY_SPONSOR, question.label === 'Yes')}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: assessedBySponsor === true
          },
          {
            char: 'N',
            label: 'No',
            selected: assessedBySponsor === false
          }
        ]}
      />
    </QuestionPage>
  );
};
export default VisaPrepFormUniEnrolmentPage1;
